import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  BookOpenCheck,
  CheckCircle2,
  FileText,
  GitBranch,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';

import { ropaAPI } from '../services/api';
import PermissionGate from '../components/PermissionGate';
import { useAuth } from '../utils/AuthContext';

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatDate(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const emptyForm = {
  name: '',
  description: '',
  owner_department: '',
  process_owner: '',
  lawful_basis: 'CONSENT',
  purpose_category: '',
  processing_purpose: '',
  data_subject_categories: [],
  data_categories: [],
  document_categories: [],
  recipients: [],
  cross_border_transfer: false,
  transfer_countries: [],
  transfer_safeguards: '',
  retention_period_days: '',
  retention_rule: '',
  security_measures: [],
  is_active: true,
};

function csvToArray(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function arrayToCsv(value) {
  return Array.isArray(value) ? value.join(', ') : '';
}

function ActivityStatusBadge({ active }) {
  return (
    <span
      className={cx(
        'rounded-full px-2.5 py-1 text-xs font-semibold ring-1',
        active
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
          : 'bg-slate-100 text-slate-600 ring-slate-200',
      )}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export default function ROPAPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('manage_ropa');

  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [recordLinks, setRecordLinks] = useState([]);

  const [filters, setFilters] = useState({
    search: '',
    lawful_basis: '',
    is_active: 'true',
  });

  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);

  const [showAutoMap, setShowAutoMap] = useState(false);
  const [autoMapForm, setAutoMapForm] = useState({
    consent_id_contains: '',
    document_type_contains: '',
    map_all: false,
  });

  const [loading, setLoading] = useState(true);
  const [linksLoading, setLinksLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadActivities = async (override = {}) => {
    setLoading(true);
    setError('');

    try {
      const params = Object.entries({ ...filters, ...override }).reduce(
        (acc, [key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );

      const res = await ropaAPI.getActivities(params);
      const results = res.data?.results || [];

      setActivities(results);

      if (!selectedActivity && results.length) {
        setSelectedActivity(results[0]);
        loadRecordLinks(results[0].id);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to load ROPA activities.',
      );
    } finally {
      setLoading(false);
    }
  };

  const loadRecordLinks = async (activityId) => {
    if (!activityId) return;

    setLinksLoading(true);
    setError('');

    try {
      const res = await ropaAPI.getRecordLinks(activityId);
      setRecordLinks(res.data?.results || []);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to load linked records.',
      );
    } finally {
      setLinksLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    return {
      total: activities.length,
      active: activities.filter((item) => item.is_active).length,
      linkedRecords: activities.reduce(
        (sum, item) => sum + Number(item.records_count || 0),
        0,
      ),
      consentBased: activities.filter((item) => item.lawful_basis === 'CONSENT').length,
    };
  }, [activities]);

  const openCreateForm = () => {
    setEditingActivity(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (activity) => {
    setEditingActivity(activity);
    setForm({
      ...emptyForm,
      ...activity,
      retention_period_days: activity.retention_period_days || '',
      data_subject_categories: activity.data_subject_categories || [],
      data_categories: activity.data_categories || [],
      document_categories: activity.document_categories || [],
      recipients: activity.recipients || [],
      transfer_countries: activity.transfer_countries || [],
      security_measures: activity.security_measures || [],
    });
    setShowForm(true);
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const buildPayload = () => ({
    ...form,
    retention_period_days:
      form.retention_period_days === '' || form.retention_period_days === null
        ? null
        : Number(form.retention_period_days),
  });

  const saveActivity = async () => {
    if (!form.name.trim()) {
      setError('Activity name is required.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = buildPayload();

      let res;

      if (editingActivity) {
        res = await ropaAPI.updateActivity(editingActivity.id, payload);
        setSuccess('Processing activity updated.');
      } else {
        res = await ropaAPI.createActivity(payload);
        setSuccess('Processing activity created.');
      }

      setShowForm(false);
      setEditingActivity(null);
      setSelectedActivity(res.data);

      await loadActivities();

      if (res.data?.id) {
        await loadRecordLinks(res.data.id);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.name?.[0] ||
          err.message ||
          'Failed to save processing activity.',
      );
    } finally {
      setSaving(false);
    }
  };

  const deactivateActivity = async (activity) => {
    const confirmed = window.confirm('Deactivate this processing activity?');
    if (!confirmed) return;

    setError('');
    setSuccess('');

    try {
      await ropaAPI.deactivateActivity(activity.id);
      setSuccess('Processing activity deactivated.');
      await loadActivities();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to deactivate processing activity.',
      );
    }
  };

  const runAutoMap = async () => {
    if (!selectedActivity) return;

    if (
      !autoMapForm.consent_id_contains &&
      !autoMapForm.document_type_contains &&
      !autoMapForm.map_all
    ) {
      setError('Provide consent filter, document type filter, or select map all.');
      return;
    }

    setAutoMapping(true);
    setError('');
    setSuccess('');

    try {
      const res = await ropaAPI.autoMapRecords(selectedActivity.id, autoMapForm);

      setSuccess(
        `Auto-map complete. Created ${res.data.created_links} links. Skipped ${res.data.skipped_existing_links}.`,
      );

      setShowAutoMap(false);
      setAutoMapForm({
        consent_id_contains: '',
        document_type_contains: '',
        map_all: false,
      });

      await loadRecordLinks(selectedActivity.id);
      await loadActivities();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Auto-map failed.',
      );
    } finally {
      setAutoMapping(false);
    }
  };

  const selectActivity = (activity) => {
    setSelectedActivity(activity);
    loadRecordLinks(activity.id);
  };

  return (
    <PermissionGate permission="view_ropa" showFallback>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-orange-50 p-3 text-orange-600 ring-1 ring-orange-100">
              <BookOpenCheck className="h-6 w-6" />
            </div>

            <div>
              <h1 className="text-2xl font-bold text-slate-950">
                ROPA
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Maintain records of processing activities mapped to PII records, consent evidence, documents, and retention.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {canManage && (
              <button
                onClick={openCreateForm}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
              >
                <Plus className="h-4 w-4" />
                Create Activity
              </button>
            )}

            <button
              onClick={() => loadActivities()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5" />
              <div>
                <p className="font-semibold">Something went wrong</p>
                <p className="mt-1 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5" />
              <div>
                <p className="font-semibold">Success</p>
                <p className="mt-1 text-sm">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Activities</p>
            <p className="mt-3 text-3xl font-bold text-slate-950">{stats.total}</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Active</p>
            <p className="mt-3 text-3xl font-bold text-emerald-600">{stats.active}</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Linked Records</p>
            <p className="mt-3 text-3xl font-bold text-orange-600">{stats.linkedRecords}</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Consent Basis</p>
            <p className="mt-3 text-3xl font-bold text-blue-600">{stats.consentBased}</p>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Search
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  placeholder="Activity, department, purpose"
                  className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Lawful Basis
              </label>
              <select
                value={filters.lawful_basis}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, lawful_basis: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                <option value="">All</option>
                <option value="CONSENT">Consent</option>
                <option value="CONTRACT">Contract</option>
                <option value="LEGAL_OBLIGATION">Legal Obligation</option>
                <option value="LEGITIMATE_USE">Legitimate Use</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Status
              </label>
              <select
                value={filters.is_active}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, is_active: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={() => loadActivities()}
              className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
            >
              Apply Filters
            </button>

            <button
              onClick={() => {
                const reset = { search: '', lawful_basis: '', is_active: 'true' };
                setFilters(reset);
                loadActivities(reset);
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <h2 className="font-bold text-slate-950">Processing Activities</h2>
              <p className="mt-1 text-sm text-slate-500">
                Select an activity to view linked records.
              </p>
            </div>

            <div className="max-h-[calc(100vh-340px)] overflow-y-auto p-3">
              {loading ? (
                <div className="flex min-h-[240px] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                </div>
              ) : activities.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                  <BookOpenCheck className="mx-auto h-8 w-8 text-orange-500" />
                  <h3 className="mt-3 font-bold text-slate-950">No activities found</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Create your first ROPA processing activity.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activities.map((activity) => {
                    const active = selectedActivity?.id === activity.id;

                    return (
                      <button
                        key={activity.id}
                        onClick={() => selectActivity(activity)}
                        className={cx(
                          'w-full rounded-2xl border p-4 text-left transition',
                          active
                            ? 'border-orange-200 bg-orange-50 shadow-sm'
                            : 'border-slate-200 bg-white hover:bg-slate-50',
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-slate-950">
                              {activity.name}
                            </p>
                            <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                              {activity.processing_purpose || activity.description || 'No purpose defined.'}
                            </p>
                          </div>

                          <ActivityStatusBadge active={activity.is_active} />
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                            {activity.lawful_basis}
                          </span>
                          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                            {activity.records_count || 0} records
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="min-w-0 rounded-3xl border border-slate-200 bg-white shadow-sm">
            {!selectedActivity ? (
              <div className="p-10 text-center text-sm text-slate-500">
                Select a processing activity.
              </div>
            ) : (
              <div>
                <div className="border-b border-slate-200 p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-bold text-slate-950">
                          {selectedActivity.name}
                        </h2>
                        <ActivityStatusBadge active={selectedActivity.is_active} />
                      </div>

                      <p className="mt-2 max-w-3xl text-sm text-slate-500">
                        {selectedActivity.processing_purpose ||
                          selectedActivity.description ||
                          'No processing purpose defined.'}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          {selectedActivity.owner_department || 'No department'}
                        </span>
                        <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                          {selectedActivity.lawful_basis}
                        </span>
                      </div>
                    </div>

                    {canManage && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openEditForm(selectedActivity)}
                          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => setShowAutoMap(true)}
                          className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                        >
                          <GitBranch className="h-4 w-4" />
                          Auto-map
                        </button>

                        {selectedActivity.is_active && (
                          <button
                            onClick={() => deactivateActivity(selectedActivity)}
                            className="rounded-xl bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 p-6 2xl:grid-cols-[1fr_360px]">
                  <div>
                    <h3 className="font-bold text-slate-950">Linked PII Records</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      These records are mapped to this processing activity.
                    </p>

                    <div className="mt-4 space-y-3">
                      {linksLoading ? (
                        <div className="flex min-h-[240px] items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                        </div>
                      ) : recordLinks.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                          <Archive className="mx-auto h-8 w-8 text-orange-500" />
                          <h3 className="mt-3 font-bold text-slate-950">No linked records</h3>
                          <p className="mt-1 text-sm text-slate-500">
                            Use auto-map to link existing PII records.
                          </p>
                        </div>
                      ) : (
                        recordLinks.map((link) => (
                          <div
                            key={link.id}
                            className="rounded-2xl border border-slate-200 p-4"
                          >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                              <div>
                                <p className="font-mono text-sm font-semibold text-slate-950">
                                  {link.pii_record}
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  User: {link.user_uuid}
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  Consent: {link.consent_id || '—'}
                                </p>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {link.consent_snapshot_available && (
                                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                                    Consent snapshot
                                  </span>
                                )}

                                <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                                  {link.documents_count} docs
                                </span>
                              </div>
                            </div>

                            <p className="mt-3 text-xs text-slate-500">
                              Match: {link.match_reason || 'Manual'} · {formatDate(link.created_at)}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <aside className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 p-5">
                      <h3 className="font-bold text-slate-950">ROPA Details</h3>

                      <dl className="mt-4 space-y-3 text-sm">
                        <div>
                          <dt className="text-slate-500">Purpose Category</dt>
                          <dd className="mt-1 font-medium text-slate-900">
                            {selectedActivity.purpose_category || '—'}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-slate-500">Retention</dt>
                          <dd className="mt-1 font-medium text-slate-900">
                            {selectedActivity.retention_period_days
                              ? `${selectedActivity.retention_period_days} days`
                              : selectedActivity.retention_rule || '—'}
                          </dd>
                        </div>

                        <div>
                          <dt className="text-slate-500">Cross-border Transfer</dt>
                          <dd className="mt-1 font-medium text-slate-900">
                            {selectedActivity.cross_border_transfer ? 'Yes' : 'No'}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <div className="rounded-2xl border border-orange-100 bg-orange-50 p-5">
                      <h3 className="font-bold text-orange-900">Data Categories</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(selectedActivity.data_categories || []).length === 0 ? (
                          <span className="text-sm text-orange-800">No categories configured.</span>
                        ) : (
                          selectedActivity.data_categories.map((item) => (
                            <span
                              key={item}
                              className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100"
                            >
                              {item}
                            </span>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 p-5">
                      <h3 className="font-bold text-slate-950">Security Measures</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(selectedActivity.security_measures || []).length === 0 ? (
                          <span className="text-sm text-slate-500">No measures configured.</span>
                        ) : (
                          selectedActivity.security_measures.map((item) => (
                            <span
                              key={item}
                              className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"
                            >
                              {item}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            )}
          </section>
        </div>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
            <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">
                    {editingActivity ? 'Edit Processing Activity' : 'Create Processing Activity'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Define purpose, lawful basis, retention, data categories, and security measures.
                  </p>
                </div>

                <button
                  onClick={() => setShowForm(false)}
                  className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Activity Name
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="Employee onboarding"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Owner Department
                    </label>
                    <input
                      value={form.owner_department || ''}
                      onChange={(e) => updateForm('owner_department', e.target.value)}
                      placeholder="HR / Finance / Legal"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Process Owner
                    </label>
                    <input
                      value={form.process_owner || ''}
                      onChange={(e) => updateForm('process_owner', e.target.value)}
                      placeholder="Owner name or team"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Lawful Basis
                    </label>
                    <select
                      value={form.lawful_basis}
                      onChange={(e) => updateForm('lawful_basis', e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    >
                      <option value="CONSENT">Consent</option>
                      <option value="CONTRACT">Contract</option>
                      <option value="LEGAL_OBLIGATION">Legal Obligation</option>
                      <option value="LEGITIMATE_USE">Legitimate Use</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Purpose Category
                    </label>
                    <input
                      value={form.purpose_category || ''}
                      onChange={(e) => updateForm('purpose_category', e.target.value)}
                      placeholder="employee_onboarding"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Retention Days
                    </label>
                    <input
                      type="number"
                      value={form.retention_period_days || ''}
                      onChange={(e) => updateForm('retention_period_days', e.target.value)}
                      placeholder="365"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Processing Purpose
                  </label>
                  <textarea
                    value={form.processing_purpose || ''}
                    onChange={(e) => updateForm('processing_purpose', e.target.value)}
                    rows={3}
                    placeholder="Describe why this personal data is processed."
                    className="w-full resize-none rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Data Subject Categories
                    </label>
                    <input
                      value={arrayToCsv(form.data_subject_categories)}
                      onChange={(e) =>
                        updateForm('data_subject_categories', csvToArray(e.target.value))
                      }
                      placeholder="employees, family members"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Data Categories
                    </label>
                    <input
                      value={arrayToCsv(form.data_categories)}
                      onChange={(e) =>
                        updateForm('data_categories', csvToArray(e.target.value))
                      }
                      placeholder="identity, contact, financial"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Document Categories
                    </label>
                    <input
                      value={arrayToCsv(form.document_categories)}
                      onChange={(e) =>
                        updateForm('document_categories', csvToArray(e.target.value))
                      }
                      placeholder="PAN, Aadhaar, payslip"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Recipients
                    </label>
                    <input
                      value={arrayToCsv(form.recipients)}
                      onChange={(e) => updateForm('recipients', csvToArray(e.target.value))}
                      placeholder="HR team, payroll vendor"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Security Measures
                    </label>
                    <input
                      value={arrayToCsv(form.security_measures)}
                      onChange={(e) =>
                        updateForm('security_measures', csvToArray(e.target.value))
                      }
                      placeholder="encryption, RBAC, audit logging"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Transfer Countries
                    </label>
                    <input
                      value={arrayToCsv(form.transfer_countries)}
                      onChange={(e) =>
                        updateForm('transfer_countries', csvToArray(e.target.value))
                      }
                      placeholder="India, UK"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <input
                    type="checkbox"
                    checked={form.cross_border_transfer}
                    onChange={(e) =>
                      updateForm('cross_border_transfer', e.target.checked)
                    }
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  />

                  <div>
                    <p className="font-semibold text-slate-900">
                      Cross-border transfer applies
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Select this if personal data is transferred outside the primary processing country.
                    </p>
                  </div>
                </label>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Retention Rule
                  </label>
                  <textarea
                    value={form.retention_rule || ''}
                    onChange={(e) => updateForm('retention_rule', e.target.value)}
                    rows={2}
                    placeholder="Retain until employment ends plus statutory period."
                    className="w-full resize-none rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5">
                <button
                  onClick={() => setShowForm(false)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  onClick={saveActivity}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Save Activity
                </button>
              </div>
            </div>
          </div>
        )}

        {showAutoMap && selectedActivity && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
            <div className="w-full max-w-xl rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">
                    Auto-map Records
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Link PII records to {selectedActivity.name}.
                  </p>
                </div>

                <button
                  onClick={() => setShowAutoMap(false)}
                  className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 px-6 py-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Consent ID contains
                  </label>
                  <input
                    value={autoMapForm.consent_id_contains}
                    onChange={(e) =>
                      setAutoMapForm((prev) => ({
                        ...prev,
                        consent_id_contains: e.target.value,
                      }))
                    }
                    placeholder="Example: onboarding"
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Document type contains
                  </label>
                  <input
                    value={autoMapForm.document_type_contains}
                    onChange={(e) =>
                      setAutoMapForm((prev) => ({
                        ...prev,
                        document_type_contains: e.target.value,
                      }))
                    }
                    placeholder="Example: PAN"
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-4">
                  <input
                    type="checkbox"
                    checked={autoMapForm.map_all}
                    onChange={(e) =>
                      setAutoMapForm((prev) => ({
                        ...prev,
                        map_all: e.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  />

                  <div>
                    <p className="font-semibold text-orange-900">
                      Map all records
                    </p>
                    <p className="mt-1 text-sm text-orange-800">
                      Use only when this processing activity applies to all current PII records.
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5">
                <button
                  onClick={() => setShowAutoMap(false)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  onClick={runAutoMap}
                  disabled={autoMapping}
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
                >
                  {autoMapping ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <GitBranch className="h-4 w-4" />
                  )}
                  Run Auto-map
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PermissionGate>
  );
}